/** Informative deal card (reference: old dashboard cards) — image, price,
 * title, key facts, confirmed features, short description, honest market
 * info. Unrated = compact neutral tag, never invented values. */
import React from "react";
import { useTranslation } from "react-i18next";
import { Check, MapPin, Share2 } from "lucide-react";
import MarketSignalBadge from "../MarketSignalBadge";
import {
  resolveMarketBadge,
  valuationTier,
  displayComparableCount,
  TIER_NEUTRAL,
} from "../../utils/marketSignal";

/* Compat helper — PublicListingPage renders the same market badge.
 * Strict, soft AND caveat ratings are all real engine results: the
 * shared badge labels each one honestly (tier "deal" = solid percent,
 * tier "estimate" = transparent chip + "Schätzung" tag, tier "neutral"
 * = grey ⓘ chip without any number). Only a listing WITHOUT any engine
 * result gets the neutral "keine Bewertung" tag. */
export function MarketBadge({ card, t }) {
  if (resolveMarketBadge(card)) return <MarketSignalBadge card={card} />;
  return (
    <span
      data-testid="no-rating-badge"
      title={t("card.noRating")}
      className="inline-flex items-center rounded-md bg-zinc-800/80 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400 ring-1 ring-zinc-700/60"
    >
      {t("card.noRatingShort")}
    </span>
  );
}

export const marketBadge = (card, t) => <MarketBadge card={card} t={t} />;

const eur = (n) => `${Math.round(n).toLocaleString("de-AT")} €`;

function factsFor(card, t) {
  const f = [];
  if (card.vertical === "auto") {
    if (card.year) f.push(`${t("card.firstReg")} ${card.year}`);
    if (typeof card.mileage_km === "number") f.push(`${card.mileage_km.toLocaleString("de-AT")} km`);
    if (typeof card.power_kw === "number") f.push(`${card.power_kw} kW (${Math.round(card.power_kw * 1.35962)} PS)`);
    if (card.fuel) f.push(t(`enums:fuel.${card.fuel}`, { defaultValue: card.fuel }));
    if (card.gearbox) f.push(t(`enums:gearbox.${card.gearbox}`, { defaultValue: card.gearbox }));
  } else {
    f.push(card.transaction_type === "rent" ? t("hero.rentTx") : t("hero.buy"));
    if (card.object_type) f.push(t(`enums:object_type.${card.object_type}`, { defaultValue: card.object_type }));
    if (typeof card.living_area_m2 === "number") f.push(`${card.living_area_m2} m²`);
    if (typeof card.rooms === "number") f.push(`${card.rooms} ${t("card.roomsShort")}`);
    if (card.year_built) f.push(`${t("search.yearBuilt")} ${card.year_built}`);
  }
  return f.filter(Boolean);
}

export default function PublicDealCard({ card, index = 0, onOpen }) {
  const { t } = useTranslation(["discover", "enums"]);
  const location = card.location || card.city || "";
  const facts = factsFor(card, t);
  const features = (card.features || []).slice(0, 3);
  const extraFeatures = Math.max(0, (card.features || []).length - 3);
  // A suppressed signal (caveat / weak / broad basis) must not re-enter
  // the card as a hard "market reference € X" promise next to a grey
  // "not reliable" chip. Counts follow the backend display contract.
  const shownComps = displayComparableCount(card);
  const showMarketRef =
    valuationTier(card) !== TIER_NEUTRAL
    && typeof card.price_vs_market_pct === "number"
    && typeof card.estimated_market_price === "number";

  const share = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/deal/${card.id}`;
    if (navigator.share) {
      navigator.share({ title: card.title, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).catch(() => {});
    }
  };

  return (
    <button
      type="button"
      data-testid={`public-deal-card-${index}`}
      onClick={() => onOpen?.(card)}
      className="group flex w-full flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/50 text-left transition-colors hover:border-zinc-600"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-800">
        {(card.image_url || card.primary_image_url) && (
          <img
            src={card.image_url || card.primary_image_url}
            alt={card.title || ""}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        )}
        {card.market_signal && typeof card.price_vs_market_pct === "number" && card.price_vs_market_pct < 0 && (
          <span className="absolute left-2 top-2 rounded-md bg-emerald-500/90 px-1.5 py-0.5 text-[11px] font-bold text-zinc-950">
            {card.price_vs_market_pct.toFixed(1).replace(".", ",")} %
          </span>
        )}
        {card.price_reduced && (
          <span className="absolute right-2 top-2 rounded-md bg-amber-500/90 px-1.5 py-0.5 text-[10px] font-bold text-zinc-950">
            {t("card.priceReduced")}
          </span>
        )}
        <span
          role="button"
          tabIndex={-1}
          data-testid={`card-share-${index}`}
          onClick={share}
          className="absolute bottom-2 right-2 rounded-full bg-zinc-950/70 p-2 text-zinc-300 opacity-0 transition-opacity hover:text-white group-hover:opacity-100"
          aria-label={t("card.share")}
        >
          <Share2 size={13} aria-hidden />
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-lg font-bold text-zinc-100" data-testid={`card-price-${index}`}>
            {typeof card.price === "number" ? eur(card.price) : "—"}
          </span>
          {card.price_reduced && typeof card.previous_price === "number" && (
            <span className="text-xs text-zinc-500 line-through">{eur(card.previous_price)}</span>
          )}
          <MarketBadge card={card} t={t} />
        </div>
        <div className="line-clamp-1 text-sm font-medium text-zinc-200">{card.title}</div>
        {facts.length > 0 && (
          <div className="text-xs text-zinc-400" data-testid={`card-facts-${index}`}>
            {facts.join(" · ")}
          </div>
        )}
        {showMarketRef && (
          <div className="text-[11px] text-zinc-500">
            {t("card.marketRef")} <span className="text-zinc-300">{eur(card.estimated_market_price)}</span>
            {shownComps !== null && (
              <> · {t("card.comparables", { count: shownComps })}</>
            )}
            {card.confidence_band_key && t(`confidence.${card.confidence_band_key}`, { defaultValue: "" }) && (
              <span data-testid={`card-confidence-${index}`}> · {t(`confidence.${card.confidence_band_key}`)}</span>
            )}
          </div>
        )}
        {card.vertical !== "auto" && card.transaction_type !== "rent" && typeof card.gross_yield_avg === "number"
          && (card.yield_confidence_class === "high" || card.yield_confidence_class === "medium") && (
          <div className="text-[11px] text-sky-300/90" data-testid={`card-yield-${index}`}>
            {t("card.grossYield", { pct: card.gross_yield_avg.toFixed(1).replace(".", ",") })}
            {typeof card.estimated_monthly_rent_avg === "number" && (
              <span className="text-zinc-500"> · {t("card.estRent", { eur: eur(card.estimated_monthly_rent_avg) })}</span>
            )}
          </div>
        )}
        {card.description_snippet && (
          <p className="line-clamp-2 text-[11px] leading-snug text-zinc-500">{card.description_snippet}</p>
        )}
        {features.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {features.map((f) => (
              <span key={f} className="inline-flex items-center gap-0.5 rounded bg-zinc-800/80 px-1.5 py-0.5 text-[10px] text-zinc-400">
                <Check size={9} className="text-emerald-500" aria-hidden />
                {t(`enums:features.${f}`, { defaultValue: f })}
              </span>
            ))}
            {extraFeatures > 0 && (
              <span className="rounded bg-zinc-800/50 px-1.5 py-0.5 text-[10px] text-zinc-500">+{extraFeatures}</span>
            )}
          </div>
        )}
        {location && (
          <div className="mt-auto flex items-center gap-1 pt-1 text-[11px] text-zinc-500">
            <MapPin size={10} aria-hidden />
            {location}
          </div>
        )}
      </div>
    </button>
  );
}
